import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, Shield, Building2, Sparkles } from "lucide-react";
import { api, CITIES_FALLBACK } from "../lib/api";
import { useCities } from "../lib/useCities";
import CenterCard from "../components/CenterCard";

const HERO_BG = "https://images.pexels.com/photos/11076203/pexels-photo-11076203.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920";

export default function Landing() {
    const [cities, setCities] = useState([]);
    const [centers, setCenters] = useState([]);
    const { cities: cityNames } = useCities();
    const [city, setCity] = useState("Makati");
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/cities").then((r) => setCities(r.data)).catch(() => setCities(CITIES_FALLBACK.map((n) => ({ name: n, count: 0 }))));
        api.get("/centers").then((r) => setCenters(r.data)).catch(() => setCenters([]));
    }, []);

    const onSearch = (e) => {
        e.preventDefault();
        navigate(`/search?city=${encodeURIComponent(city)}`);
    };

    return (
        <div className="pt-16">
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
                </div>
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16 pt-28 w-full text-white">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-8">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-label">
                                <span className="h-px w-8 bg-white/70" />
                                Flexible Workspace Group · Philippines
                            </div>
                            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95]">
                                The address<br />
                                your business<br />
                                <span className="text-[#FF6A13]">wears well.</span>
                            </h1>
                            <p className="mt-6 max-w-xl text-white/80 text-base leading-relaxed">
                                A curated marketplace of virtual business addresses — premium buildings,
                                photogenic offices, and conference rooms on recall-rich streets across the Philippines.
                            </p>
                        </div>
                        <div className="md:col-span-4">
                            <div className="text-[10px] uppercase tracking-label text-white/60 mb-2">By the numbers</div>
                            <div className="grid grid-cols-2 gap-px bg-white/20">
                                <div className="bg-black/60 p-4">
                                    <div className="font-display text-4xl font-semibold">{cities.length || 9}</div>
                                    <div className="text-[10px] uppercase tracking-label text-white/60 mt-1">Cities</div>
                                </div>
                                <div className="bg-black/60 p-4">
                                    <div className="font-display text-4xl font-semibold">{centers.length}</div>
                                    <div className="text-[10px] uppercase tracking-label text-white/60 mt-1">Addresses</div>
                                </div>
                                <div className="bg-black/60 p-4">
                                    <div className="font-display text-4xl font-semibold">PDC</div>
                                    <div className="text-[10px] uppercase tracking-label text-white/60 mt-1">or Cash</div>
                                </div>
                                <div className="bg-black/60 p-4">
                                    <div className="font-display text-4xl font-semibold">1-yr</div>
                                    <div className="text-[10px] uppercase tracking-label text-white/60 mt-1">Min term</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SEARCH */}
                    <form
                        onSubmit={onSearch}
                        data-testid="hero-search-form"
                        className="mt-12 bg-white text-zinc-950 flex flex-col md:flex-row items-stretch gap-px shadow-2xl"
                    >
                        <div className="flex-1 flex items-center gap-3 px-5 py-4 bg-white">
                            <Search className="h-5 w-5 text-zinc-500" strokeWidth={1.5} />
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-label text-zinc-500">Pick a city</div>
                                <select
                                    data-testid="hero-city-select"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    className="w-full bg-transparent focus:outline-none font-display text-lg font-medium"
                                >
                                    {cityNames.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <button
                            type="submit"
                            data-testid="hero-search-btn"
                            className="bg-zinc-950 text-white px-8 py-4 flex items-center justify-center gap-2 hover:bg-[#FF6A13] transition-colors"
                        >
                            Find an address <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </form>
                </div>
            </section>

            {/* CITIES */}
            <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-label text-zinc-500">01 · Cities</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-2">Where would you like to be?</h2>
                    </div>
                    <Link to="/search" data-testid="see-all-cities-link" className="text-sm text-zinc-700 underline underline-offset-4 hover:text-zinc-950">See all addresses →</Link>
                </div>
                <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                    {cities.map((c, i) => (
                        <Link
                            key={c.name}
                            to={`/search?city=${encodeURIComponent(c.name)}`}
                            data-testid={`city-tile-${c.name}`}
                            className="group bg-white p-6 md:p-8 hover:bg-zinc-950 hover:text-white transition-colors relative overflow-hidden"
                        >
                            <div className="text-[10px] uppercase tracking-label text-zinc-500 group-hover:text-white/60">0{i + 1}</div>
                            <div className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-3">{c.name}</div>
                            <div className="mt-4 flex items-center justify-between text-sm">
                                <span className="text-zinc-500 group-hover:text-white/70">
                                    {c.count} {c.count === 1 ? "address" : "addresses"}
                                </span>
                                <ArrowRight className="h-4 w-4 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" strokeWidth={1.5} />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* FEATURED */}
            {centers.length > 0 && (
                <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-label text-zinc-500">02 · Featured</div>
                            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-2">Addresses people remember.</h2>
                        </div>
                    </div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {centers.slice(0, 6).map((c, i) => (
                            <CenterCard key={c.center_id} center={c} index={i} />
                        ))}
                    </div>
                </section>
            )}

            {/* WHY */}
            <section className="bg-zinc-950 text-white">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="text-[10px] uppercase tracking-label text-white/60">03 · Why FWG</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05]">One marketplace. One standard.</h2>
                        <p className="mt-6 text-white/70 max-w-md">
                            Whether it's Loud Crowd in Legazpi or Prime Coworking Lab in BGC — every listing is held to
                            FWG's verification standard. You see the photos. FWG handles the rest.
                        </p>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/15">
                        {[
                            { icon: Sparkles, title: "Curated", t: "Only centers approved by FWG make the listings." },
                            { icon: Shield, title: "Confidential", t: "Provider details stay private. FWG is your single point of contact." },
                            { icon: Building2, title: "Recognizable", t: "Addresses on streets your clients already know — Ayala, BGC, Ortigas." },
                        ].map(({ icon: Icon, title, t }) => (
                            <div key={title} className="bg-zinc-950 p-6">
                                <Icon className="h-5 w-5" strokeWidth={1.5} />
                                <div className="font-display text-xl font-semibold mt-6">{title}</div>
                                <div className="mt-2 text-sm text-white/70">{t}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROVIDER CTA */}
            <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                <div className="md:col-span-7">
                    <div className="text-[10px] uppercase tracking-label text-zinc-500">04 · For operators</div>
                    <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3">
                        Run a coworking space?<br /> List it on FWG.
                    </h2>
                    <p className="mt-4 text-zinc-600 max-w-lg">
                        Enroll your center, upload up to 6 photos, tell us what makes your address distinctive.
                        Our team reviews and publishes it across the marketplace.
                    </p>
                </div>
                <div className="md:col-span-5">
                    <Link
                        to="/enroll"
                        data-testid="enroll-cta"
                        className="group inline-flex items-center justify-between w-full bg-[#FF6A13] text-white px-6 py-5 hover:bg-orange-600 transition-colors"
                    >
                        <span className="font-display text-lg font-medium">Enroll your center</span>
                        <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
